import { Component, DoCheck, ElementRef, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-article-numbers-field',
  templateUrl: './article-numbers-field.component.html',
  imports: [MatChipsModule, MatIconModule, MatAutocompleteModule, TranslateModule],
  providers: [{ provide: MatFormFieldControl, useExisting: ArticleNumbersFieldComponent }],
})
export class ArticleNumbersFieldComponent
  implements ControlValueAccessor, MatFormFieldControl<string[]>, OnDestroy, DoCheck
{
  static nextId = 0;
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject(ElementRef);
  public readonly ngControl = inject(NgControl);

  @Input() placeholder!: string;
  @Input() prefixIcon?: string; // Optional prefix icon (e.g., 'search'), empty/hidden by default
  // When true, an entry is split ONLY on comma (spaces kept) so multi-word values like "palm oil"
  // stay one chip. Default false keeps the article-number behaviour of splitting on any whitespace.
  @Input() allowSpaces = false;
  /**
   * Optional autocomplete pool. Free-text entry stays the primary path — the list only guards
   * against the duplicate spellings free text invites ("Kritisch" / "kritisch" / "kritsch").
   * Empty means no panel at all, which is the correct empty state, not a broken one.
   */
  @Input() suggestions: string[] = [];

  /**
   * The field is used twice on the same form (article numbers and tags), so the test id cannot
   * be a constant — two elements sharing one would make every selector ambiguous.
   */
  @Input() testId = 'article-number-input';
  @ViewChild('input') inputElement!: ElementRef<HTMLInputElement>;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  articleNumbers: string[] = [];

  // MatFormFieldControl properties
  readonly id = `article-numbers-field-${ArticleNumbersFieldComponent.nextId++}`;
  readonly stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'article-numbers-field';
  autofilled = false;

  private _disabled = false;
  private _required = false;
  private onChange = (value: string[]) => {};
  private onTouched = () => {};

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor.monitor(this.elementRef, true).subscribe((origin) => {
      if (origin) {
        this.onFocusIn();
      } else {
        this.onFocusOut();
      }
    });
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
    this.stateChanges.next();
  }

  get required(): boolean {
    return this._required;
  }

  set required(_value: boolean) {
    this._required = _value;
    this.stateChanges.next();
  }

  get empty(): boolean {
    return this.articleNumbers.length === 0;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return !!(this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched));
  }

  get value(): string[] | null {
    return this.articleNumbers.length ? this.articleNumbers : null;
  }

  set value(articles: string[] | null) {
    this.articleNumbers = articles || [];
    this.stateChanges.next();
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(): void {
    if (!this.elementRef.nativeElement.contains(document.activeElement as Node)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  setDescribedByIds(_ids: string[]): void {
    // Implementation for screen readers
  }

  onContainerClick(): void {
    if (this.inputElement) {
      this.focusMonitor.focusVia(this.inputElement, 'program');
    }
  }

  private updateErrorState(): void {
    const oldState = this.errorState;
    const newState = !!(this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched));

    if (newState !== oldState) {
      this.stateChanges.next();
    }
  }

  writeValue(value: string[]): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  addArticleNumber(event: MatChipInputEvent): void {
    this.addArticleNumbers(event.value);
    const input = event.input;
    if (input) {
      input.value = '';
    }
  }

  private addArticleNumbers(rawValue: string) {
    const value = rawValue?.trim();
    if (!value) {
      return;
    }

    const entries = value.split(this.allowSpaces ? ',' : /[\s,]+/);
    for (const entry of entries) {
      const trimmed = entry.trim();
      if (trimmed && !this.articleNumbers.includes(trimmed)) {
        this.articleNumbers.push(trimmed);
      }
    }
    this.onChange([...this.articleNumbers]);
    this.stateChanges.next();
  }

  /** Suggestions minus what is already on the field, narrowed by whatever is typed so far. */
  filteredSuggestions(): string[] {
    const typed = this.inputElement?.nativeElement.value?.trim().toLowerCase() ?? '';
    return this.suggestions
      .filter((s) => !this.articleNumbers.includes(s))
      .filter((s) => !typed || s.toLowerCase().includes(typed));
  }

  selectSuggestion(event: MatAutocompleteSelectedEvent): void {
    this.addArticleNumbers(event.option.value);
    if (this.inputElement) {
      this.inputElement.nativeElement.value = '';
    }
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    this.addArticleNumbers(pastedText);
    if (this.inputElement) {
      this.inputElement.nativeElement.value = '';
    }
  }

  removeArticleNumber(articleNumber: string): void {
    const index = this.articleNumbers.indexOf(articleNumber);
    if (index >= 0) {
      this.articleNumbers.splice(index, 1);
      this.onChange([...this.articleNumbers]);
      this.stateChanges.next();
    }
  }

  addPendingArticleNumber(event: FocusEvent): void {
    this.onFocusOut();
    if (!event.target) {
      return;
    }
    const input = event.target as HTMLInputElement;
    this.addArticleNumbers(input.value);
    input.value = '';
  }
}
